/**
 * BelgeSablonlariPage.jsx — Sistem Ayarları › Belge Şablonları (backlog #259, 2026-10-11)
 * Route: /Management/SistemAyarlari/BelgeSablonlari   İzin: page.sistem (uçlar da yalnızca personel, #259)
 *
 * Firmaya VERDİĞİMİZ dosyalar (document_template): boş formlar (indirilip imzalanıp geri yüklenen — KVKK,
 * Taahhütname, 99 İşveren İSG Görevlendirmesi…) ve örnekler (yükleme kutusunun yanında gösterilen). Firma
 * portalı yalnızca okur (#251, "Şablonu indir"); bu sayfa onları yayımlar, yayından kaldırır, yeniden yayınlar
 * ve görünen adını değiştirir. Sözleşme: FDS-Backend guides/FE-DocumentTemplateController-guide.md.
 *
 *   - Bir YUVA = (belge türü, fuar ya da tüm fuarlar, dil, tür). Yuvada tek etkin satır olur: yeni yükleme
 *     etkin olanı yayından kaldırır ve sürümü n+1 yapar; yeniden yayınlamak da yuvadaki etkin olanı kaldırır.
 *     Sayfa ikisinde de önce uyarır.
 *   - "Yayından kaldır" YUMUŞAKTIR: hiçbir şey silinmez, geri alınabilir, dosya adresiyle indirilmeye devam
 *     eder, toplanmış imzalı kopyalara dokunmaz. Bu yüzden düğme "Sil" değildir.
 *   - Yeniden hedefleme (tür / fuar / dil değiştirme) ilk sürümde yok: hedef yuvayı temizlemediği için
 *     yuvada iki etkin satır bırakabilir. Gerekirse yeni sürüm yükleyin.
 *   - Dosya boyutu ve uzantıları Sistem Ayarları › Dosya Ayarları › Belge şablonları'ndan gelir (#259);
 *     sunucu da aynısını denetler.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container, Card, CardBody, Table, Button, Badge, Alert, Spinner, Modal, ModalHeader, ModalBody, ModalFooter,
  Label, Input, Row, Col, FormGroup,
} from "reactstrap";
import { Upload, Pencil, EyeOff, RotateCcw, Download, FileText } from "lucide-react";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import {
  listAllTemplates, getTemplateFileTypes, publishTemplate, updateTemplate, retireTemplate,
  SABLON_TURU, SABLON_TURU_ADI, DIL_ADI, kindKeyToTur, sablonTarihi, sablonIndirmeAdresi,
} from "../../../services/documentTemplateService";
import { getDosyaOzet } from "../../../services/systemSettingsService";
import { fuarService } from "../../../services/fuarService";
import { useTranslation } from "react-i18next";
import i18n, { currentLocaleTag } from "../../../i18n";

/** file_type.parent_id: firma belgeleri (listede önce gelir). */
const FIRMA_BELGELERI = 47;
/** Dosya Ayarları kategorisi (FDS-Backend SystemSettingConstant.DOSYA_BELGE_SABLONU). */
const DOSYA_KATEGORISI = "belge_sablonu";
/** Dosya Ayarları okunamazsa: sunucunun dosya tavanı (spring.servlet.multipart.max-file-size). */
const YEDEK_MAX_MB = 25;

const yuvaAnahtari = (fileTypeId, fuarId, languageId, tur) => `${fileTypeId}|${fuarId ?? ""}|${languageId}|${tur}`;
const satirYuvasi = (t) => yuvaAnahtari(t.fileTypeId, t.fuarId, t.languageId, kindKeyToTur(t.kindKey));
const fmtTarih = (s) => sablonTarihi(s)?.toLocaleString(currentLocaleTag()) ?? "—";
const uzantiOf = (ad) => (ad && ad.includes(".") ? ad.slice(ad.lastIndexOf(".") + 1).toLowerCase() : "");

function fuarAdi(fuarlar, fuarId) {
  if (fuarId == null) return i18n.t("ayarlar:belgeSablonlari.tumFuarlar");
  const f = fuarlar.find((x) => Number(x.fuarId) === Number(fuarId));
  return f ? (f.fuarShortName || f.fuarName || `Fuar #${fuarId}`) : `Fuar #${fuarId}`;
}

/** Belge türlerini üst kategoriye göre gruplar; firma belgeleri önce. */
function turGruplari(turler) {
  const gruplar = new Map();
  for (const t of turler) {
    const p = Number(t.parentId) || 0;
    if (!gruplar.has(p)) gruplar.set(p, { parentId: p, ad: p === 0 ? i18n.t("ayarlar:belgeSablonlari.anaKategoriler") : (t.parentName || `#${p}`), turler: [] });
    gruplar.get(p).turler.push(t);
  }
  return [...gruplar.values()].sort((a, b) =>
    (a.parentId === FIRMA_BELGELERI ? -1 : b.parentId === FIRMA_BELGELERI ? 1 : 0) || (a.parentId - b.parentId));
}

function TurSecici({ id, turler, value, onChange, bosEtiket }) {
  const gruplar = useMemo(() => turGruplari(turler), [turler]);
  return (
    <Input id={id} type="select" bsSize="sm" value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{bosEtiket}</option>
      {gruplar.map((g) => (
        <optgroup key={g.parentId} label={g.ad}>
          {g.turler.map((t) => <option key={t.fileTypeId} value={t.fileTypeId}>{t.fileTypeName} (#{t.fileTypeId})</option>)}
        </optgroup>
      ))}
    </Input>
  );
}

// ─── Yeni şablon yükle ──────────────────────────────────────────────────────
function YukleModal({ acik, kapat, turler, fuarlar, sinir, sablonlar, onYayimlandi }) {
  const { t: tx } = useTranslation("ayarlar");
  const [dosya, setDosya] = useState(null);
  const [fileTypeId, setFileTypeId] = useState("");
  const [fuarId, setFuarId] = useState("");
  const [languageId, setLanguageId] = useState("1");
  const [tur, setTur] = useState(String(SABLON_TURU.BOS_FORM));
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState(null);

  const maxMb = Number(sinir?.etkinMaxMb) || YEDEK_MAX_MB;
  const uzantilar = sinir?.uzantilar || [];
  const dosyaHatasi = (() => {
    if (!dosya) return null;
    if (dosya.size > maxMb * 1024 * 1024) return tx("belgeSablonlari.dosyaCokBuyukEnFazla", { maxMb });
    if (uzantilar.length && !uzantilar.includes(uzantiOf(dosya.name))) {
      return tx("belgeSablonlari.gecersizDosyaTipiIzinVerilenler", { v: uzantilar.join(", ") });
    }
    return null;
  })();

  const secilenTur = turler.find((t) => String(t.fileTypeId) === String(fileTypeId));
  // Yuvada etkin bir şablon varsa yayımlamak onu kaldırır; yeni sürüm = yuvanın en büyük sürümü + 1.
  const yuva = fileTypeId ? yuvaAnahtari(Number(fileTypeId), fuarId ? Number(fuarId) : null, Number(languageId), Number(tur)) : null;
  const yuvadakiler = yuva ? sablonlar.filter((t) => satirYuvasi(t) === yuva) : [];
  const etkin = yuvadakiler.find((t) => t.isActive);
  const yeniSurum = yuvadakiler.reduce((m, t) => Math.max(m, Number(t.templateVersion) || 0), 0) + 1;

  function sifirla() {
    setDosya(null); setFileTypeId(""); setFuarId(""); setLanguageId("1");
    setTur(String(SABLON_TURU.BOS_FORM)); setHata(null);
  }

  async function gonder() {
    setGonderiliyor(true);
    setHata(null);
    try {
      const yeni = await publishTemplate({
        file: dosya, fileTypeId: Number(fileTypeId), fuarId: fuarId ? Number(fuarId) : null,
        languageId: Number(languageId), templateKind: Number(tur),
      });
      sifirla();
      onYayimlandi(yeni);
    } catch (e) {
      setHata(e.message);
    } finally {
      setGonderiliyor(false);
    }
  }

  return (
    <Modal isOpen={acik} toggle={() => !gonderiliyor && kapat()} centered size="lg">
      <ModalHeader toggle={() => !gonderiliyor && kapat()}>{tx("belgeSablonlari.yeniSablonYukle")}</ModalHeader>
      <ModalBody>
        <FormGroup>
          <Label for="sablon-dosya" className="small mb-1">{tx("belgeSablonlari.dosya")}</Label>
          <Input
            id="sablon-dosya" type="file" bsSize="sm"
            accept={uzantilar.length ? uzantilar.map((u) => `.${u}`).join(",") : undefined}
            invalid={!!dosyaHatasi}
            onChange={(e) => setDosya(e.target.files?.[0] || null)}
          />
          <div className={`small mt-1 ${dosyaHatasi ? "text-danger" : "text-muted"}`}>
            {dosyaHatasi || tx("belgeSablonlari.enFazlaMbDosyaAyarlari", { maxMb, v: uzantilar.length ? ` · ${uzantilar.join(", ")}` : "" })}
          </div>
        </FormGroup>
        <Row className="g-2">
          <Col md={6}>
            <FormGroup>
              <Label for="sablon-tur" className="small mb-1">{tx("belgeSablonlari.belgeTuru")}</Label>
              <TurSecici id="sablon-tur" turler={turler} value={fileTypeId} onChange={setFileTypeId} bosEtiket={tx("belgeSablonlari.secin")} />
            </FormGroup>
          </Col>
          <Col md={6}>
            <FormGroup>
              <Label for="sablon-fuar" className="small mb-1">{tx("belgeSablonlari.kapsam")}</Label>
              <Input id="sablon-fuar" type="select" bsSize="sm" value={fuarId} onChange={(e) => setFuarId(e.target.value)}>
                <option value="">{tx("belgeSablonlari.tumFuarlar")}</option>
                {fuarlar.map((f) => <option key={f.fuarId} value={f.fuarId}>{f.fuarName || f.fuarShortName || `#${f.fuarId}`}</option>)}
              </Input>
            </FormGroup>
          </Col>
          <Col xs={6} md={3}>
            <FormGroup>
              <Label for="sablon-dil" className="small mb-1">{tx("belgeSablonlari.dil")}</Label>
              <Input id="sablon-dil" type="select" bsSize="sm" value={languageId} onChange={(e) => setLanguageId(e.target.value)}>
                <option value="1">{tx("belgeSablonlari.tr")}</option>
                <option value="2">{tx("belgeSablonlari.en")}</option>
              </Input>
            </FormGroup>
          </Col>
          <Col xs={6} md={3}>
            <FormGroup>
              <Label for="sablon-turu" className="small mb-1">{tx("belgeSablonlari.tur")}</Label>
              <Input id="sablon-turu" type="select" bsSize="sm" value={tur} onChange={(e) => setTur(e.target.value)}>
                <option value={SABLON_TURU.BOS_FORM}>{tx("belgeSablonlari.bosForm")}</option>
                <option value={SABLON_TURU.ORNEK}>{tx("belgeSablonlari.ornek")}</option>
              </Input>
            </FormGroup>
          </Col>
        </Row>
        <div className="text-muted small">
          <b>{tx("belgeSablonlari.bosForm")}</b>{tx("belgeSablonlari.firmaIndirirImzalarAyniBelge")}{" "}<b>{tx("belgeSablonlari.ornek")}</b>{tx("belgeSablonlari.yuklemeKutusununYanindaGosterilirGeri")}
        </div>
        {/* active_fuar_needed bir TINYINT(1): sürücüye göre 1 ya da true gelir. */}
        {(secilenTur?.activeFuarNeeded === true || Number(secilenTur?.activeFuarNeeded) === 1) && !fuarId && (
          <Alert color="info" className="py-2 small mt-3 mb-0">
            {tx("belgeSablonlari.buBelgeTuruFuaraOzeldir")}
          </Alert>
        )}
        {etkin && (
          <Alert color="warning" className="py-2 small mt-3 mb-0">
            {tx("belgeSablonlari.buYuvadaEtkinBirSablon")}{" "}<b>v{etkin.templateVersion}</b> ({etkin.fileGivenName || tx("belgeSablonlari.adsiz")}{tx("belgeSablonlari.yayimlarsanizOYayindanKalkarVe")}{" "}<b>v{yeniSurum}</b>{" "}{tx("belgeSablonlari.olurEskiSurumListedeKalir")}
          </Alert>
        )}
        {hata && <Alert color="danger" className="py-2 small mt-3 mb-0">{hata}</Alert>}
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={kapat} disabled={gonderiliyor}>{tx("belgeSablonlari.vazgec")}</Button>
        <Button color="primary" onClick={gonder} disabled={gonderiliyor || !dosya || !!dosyaHatasi || !fileTypeId}>
          {gonderiliyor ? <Spinner size="sm" className="me-1" /> : <Upload size={14} className="me-1" />}
          {etkin ? tx("belgeSablonlari.vOlarakYayimla", { yeniSurum }) : tx("belgeSablonlari.yayimla")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

// ─── Sayfa ──────────────────────────────────────────────────────────────────
export default function BelgeSablonlariPage() {
  const { t: tx } = useTranslation("ayarlar");
  const [sablonlar, setSablonlar] = useState(null);
  const [yuklemeHatasi, setYuklemeHatasi] = useState(null);
  const [surum, setSurum] = useState(0);
  const [turler, setTurler] = useState([]);
  const [fuarlar, setFuarlar] = useState([]);
  const [sinir, setSinir] = useState(null);
  const [filtre, setFiltre] = useState({ fileTypeId: "", tur: "", dil: "", kapsam: "", yalnizAktif: false });
  const [mesaj, setMesaj] = useState(null);
  const [yukleAcik, setYukleAcik] = useState(false);
  const [islem, setIslem] = useState(null);        // { tip: 'kaldir' | 'yayinla' | 'ad', sablon }
  const [yeniAd, setYeniAd] = useState("");
  const [isleniyor, setIsleniyor] = useState(false);
  const [islemHatasi, setIslemHatasi] = useState(null);

  useEffect(() => { document.title = i18n.t("ayarlar:belgeSablonlari.belgeSablonlariFds"); }, []);

  // KURAL: fetch Effect içindeki async fonksiyonda (react-hooks/set-state-in-effect).
  useEffect(() => {
    let ignore = false;
    async function run() {
      try {
        // Tablo küçük: hepsini bir kez çek, süzmeyi istemcide yap (yuva uyarıları da tüm satırları ister).
        const list = await listAllTemplates();
        if (!ignore) { setSablonlar(list); setYuklemeHatasi(null); }
      } catch (e) {
        if (!ignore) { setYuklemeHatasi(e.message); setSablonlar([]); }
      }
    }
    run();
    return () => { ignore = true; };
  }, [surum]);

  useEffect(() => {
    let ignore = false;
    async function run() {
      const [t, f, o] = await Promise.allSettled([
        getTemplateFileTypes(), fuarService.getSelectableFuarList(), getDosyaOzet(),
      ]);
      if (ignore) return;
      if (t.status === "fulfilled") setTurler(t.value);
      if (f.status === "fulfilled") setFuarlar(f.value || []);
      if (o.status === "fulfilled") setSinir((o.value?.kategoriler || []).find((k) => k.kod === DOSYA_KATEGORISI) || null);
    }
    run();
    return () => { ignore = true; };
  }, []);

  const turBilgisi = useMemo(() => new Map(turler.map((t) => [Number(t.fileTypeId), t])), [turler]);

  const gruplar = useMemo(() => {
    if (!sablonlar) return [];
    const suzulmus = sablonlar.filter((t) =>
      (!filtre.fileTypeId || String(t.fileTypeId) === filtre.fileTypeId)
      && (!filtre.tur || String(kindKeyToTur(t.kindKey)) === filtre.tur)
      && (!filtre.dil || String(t.languageId) === filtre.dil)
      && (!filtre.kapsam || (filtre.kapsam === "genel" ? t.fuarId == null : String(t.fuarId) === filtre.kapsam))
      && (!filtre.yalnizAktif || t.isActive));
    const byTur = new Map();
    for (const t of suzulmus) {
      if (!byTur.has(t.fileTypeId)) byTur.set(t.fileTypeId, []);
      byTur.get(t.fileTypeId).push(t);
    }
    const out = [...byTur.entries()].map(([fileTypeId, satirlar]) => {
      const bilgi = turBilgisi.get(Number(fileTypeId));
      satirlar.sort((a, b) => (kindKeyToTur(a.kindKey) - kindKeyToTur(b.kindKey)) || (a.languageId - b.languageId)
        || ((a.fuarId ?? -1) - (b.fuarId ?? -1)) || ((b.templateVersion || 0) - (a.templateVersion || 0)));
      return {
        fileTypeId,
        ad: bilgi?.fileTypeName || satirlar[0].fileTypeName || `#${fileTypeId}`,
        ust: bilgi?.parentName || null,
        firma: Number(bilgi?.parentId) === FIRMA_BELGELERI,
        satirlar,
      };
    });
    return out.sort((a, b) => (Number(b.firma) - Number(a.firma)) || a.ad.localeCompare(b.ad, "tr"));
  }, [sablonlar, filtre, turBilgisi]);

  const kapsamSecenekleri = useMemo(() => {
    const ids = [...new Set((sablonlar || []).map((t) => t.fuarId).filter((x) => x != null))];
    return ids.map((id) => ({ id, ad: fuarAdi(fuarlar, id) })).sort((a, b) => a.ad.localeCompare(b.ad, "tr"));
  }, [sablonlar, fuarlar]);

  const etkinSayisi = (sablonlar || []).filter((t) => t.isActive).length;

  function islemAc(tip, sablon) {
    setIslem({ tip, sablon });
    setIslemHatasi(null);
    setYeniAd(sablon.fileGivenName || "");
  }

  async function islemiYap() {
    const { tip, sablon } = islem;
    setIsleniyor(true);
    setIslemHatasi(null);
    try {
      if (tip === "kaldir") await retireTemplate(sablon.id);
      else if (tip === "yayinla") await updateTemplate(sablon.id, { isActive: true });
      else if (tip === "ad") await updateTemplate(sablon.id, { fileGivenName: String(yeniAd).trim() });
      const ad = sablon.fileGivenName || `#${sablon.id}`;
      setMesaj({
        color: "success",
        text: tip === "kaldir" ? tx("belgeSablonlari.yayindanKaldirildi", { ad })
          : tip === "yayinla" ? tx("belgeSablonlari.vYenidenYayinlandi", { ad, templateVersion: sablon.templateVersion })
            : tx("belgeSablonlari.gorunenAdGuncellendi"),
      });
      setIslem(null);
      setSurum((n) => n + 1);
    } catch (e) {
      setIslemHatasi(e.message);
    } finally {
      setIsleniyor(false);
    }
  }

  // İşlem penceresinin uyarıları: yuvadaki etkin satır (yeniden yayınlarken kalkar) ve genel yedek
  // (fuara özel bir şablon kalkınca o fuarda genel olan görünür).
  const islemYuvasi = islem ? satirYuvasi(islem.sablon) : null;
  const yuvadakiEtkin = islem
    ? (sablonlar || []).find((t) => t.isActive && t.id !== islem.sablon.id && satirYuvasi(t) === islemYuvasi)
    : null;
  const genelYedek = islem && islem.sablon.fuarId != null
    ? (sablonlar || []).find((t) => t.isActive && t.fuarId == null
      && satirYuvasi(t) === yuvaAnahtari(islem.sablon.fileTypeId, null, islem.sablon.languageId, kindKeyToTur(islem.sablon.kindKey)))
    : null;

  return (
    <div className="page-content">
      <Container fluid>
        <BreadCrumb title={tx("belgeSablonlari.belgeSablonlari")} pageTitle={tx("belgeSablonlari.sistemAyarlari")} />

        <Alert color="info" className="small">
          {tx("belgeSablonlari.firmalarinIndirdigi")}{" "}<b>{tx("belgeSablonlari.bosFormlar")}</b>{" "}{tx("belgeSablonlari.indiripImzalayipAyniBelgeTuru")}{" "}<b>{tx("belgeSablonlari.ornekler")}</b>{tx("belgeSablonlari.yayimlananBosFormFirmaPortalinda")}
        </Alert>
        {mesaj && <Alert color={mesaj.color} className="py-2" toggle={() => setMesaj(null)}>{mesaj.text}</Alert>}

        <Card className="mb-3">
          <CardBody className="py-3">
            <Row className="g-2 align-items-end">
              <Col md={4} lg={3}>
                <Label for="f-tur" className="small mb-1">{tx("belgeSablonlari.belgeTuru")}</Label>
                <TurSecici id="f-tur" turler={turler} value={filtre.fileTypeId}
                  onChange={(v) => setFiltre((f) => ({ ...f, fileTypeId: v }))} bosEtiket={tx("common:tumu")} />
              </Col>
              <Col xs={6} md={2}>
                <Label for="f-turu" className="small mb-1">{tx("belgeSablonlari.tur")}</Label>
                <Input id="f-turu" type="select" bsSize="sm" value={filtre.tur} onChange={(e) => setFiltre((f) => ({ ...f, tur: e.target.value }))}>
                  <option value="">{tx("common:tumu")}</option>
                  <option value={SABLON_TURU.BOS_FORM}>{tx("belgeSablonlari.bosForm")}</option>
                  <option value={SABLON_TURU.ORNEK}>{tx("belgeSablonlari.ornek")}</option>
                </Input>
              </Col>
              <Col xs={6} md={2} lg={1}>
                <Label for="f-dil" className="small mb-1">{tx("belgeSablonlari.dil")}</Label>
                <Input id="f-dil" type="select" bsSize="sm" value={filtre.dil} onChange={(e) => setFiltre((f) => ({ ...f, dil: e.target.value }))}>
                  <option value="">{tx("common:tumu")}</option>
                  <option value="1">{tx("belgeSablonlari.tr")}</option>
                  <option value="2">{tx("belgeSablonlari.en")}</option>
                </Input>
              </Col>
              <Col md={4} lg={3}>
                <Label for="f-kapsam" className="small mb-1">{tx("belgeSablonlari.kapsam")}</Label>
                <Input id="f-kapsam" type="select" bsSize="sm" value={filtre.kapsam} onChange={(e) => setFiltre((f) => ({ ...f, kapsam: e.target.value }))}>
                  <option value="">{tx("common:tumu")}</option>
                  <option value="genel">{tx("belgeSablonlari.tumFuarlarGenel")}</option>
                  {kapsamSecenekleri.map((k) => <option key={k.id} value={k.id}>{k.ad}</option>)}
                </Input>
              </Col>
              <Col xs="auto" className="d-flex align-items-center pb-1">
                <div className="form-check mb-0">
                  <Input id="f-aktif" type="checkbox" className="form-check-input" checked={filtre.yalnizAktif}
                    onChange={(e) => setFiltre((f) => ({ ...f, yalnizAktif: e.target.checked }))} />
                  <Label for="f-aktif" className="form-check-label small">{tx("belgeSablonlari.yalnizcaEtkin")}</Label>
                </div>
              </Col>
              <Col xs="auto" className="ms-auto">
                <Button color="primary" size="sm" onClick={() => { setYukleAcik(true); setMesaj(null); }}>
                  <Upload size={14} className="me-1" />{tx("belgeSablonlari.yeniSablonYukle")}
                </Button>
              </Col>
            </Row>
          </CardBody>
        </Card>

        {yuklemeHatasi && <Alert color="danger">{yuklemeHatasi}</Alert>}
        {sablonlar == null ? (
          <div className="text-center py-5"><Spinner color="primary" /></div>
        ) : gruplar.length === 0 ? (
          !yuklemeHatasi && (
            <div className="text-muted">
              {sablonlar.length === 0 ? tx("belgeSablonlari.henuzHicSablonYayimlanmadi") : tx("belgeSablonlari.buSuzgeceUyanSablonYok")}
            </div>
          )
        ) : (
          <Card>
            <CardBody className="p-0">
              <div className="px-3 py-2 small text-muted border-bottom">
                {tx("belgeSablonlari.sablonEtkin", { length: sablonlar.length, etkinSayisi })}
              </div>
              <div className="table-responsive">
                <Table className="align-middle mb-0" size="sm">
                  <thead className="text-muted small">
                    <tr>
                      <th className="ps-3">{tx("belgeSablonlari.tur")}</th><th>{tx("belgeSablonlari.dil")}</th><th>{tx("belgeSablonlari.kapsam")}</th><th className="text-center">{tx("belgeSablonlari.surum")}</th>
                      <th>{tx("belgeSablonlari.durum")}</th><th>{tx("belgeSablonlari.dosya")}</th><th>{tx("belgeSablonlari.yukleyen")}</th><th>{tx("belgeSablonlari.yuklendi")}</th><th />
                    </tr>
                  </thead>
                  <tbody>
                    {gruplar.map((g) => (
                      <React.Fragment key={g.fileTypeId}>
                        <tr className="table-light">
                          <td colSpan={9} className="ps-3 fw-semibold">
                            <FileText size={14} className="me-1" />{g.ad}
                            <span className="text-muted fw-normal small ms-2">#{g.fileTypeId}{g.ust ? ` · ${g.ust}` : ""}</span>
                          </td>
                        </tr>
                        {g.satirlar.map((t) => {
                          const adres = sablonIndirmeAdresi(t);
                          return (
                            <tr key={t.id} className={t.isActive ? "" : "text-muted"}>
                              <td className="ps-3">{SABLON_TURU_ADI[kindKeyToTur(t.kindKey)]}</td>
                              <td>{DIL_ADI[t.languageId] || t.languageId}</td>
                              <td>{fuarAdi(fuarlar, t.fuarId)}</td>
                              <td className="text-center">v{t.templateVersion}</td>
                              <td>
                                {t.isActive
                                  ? <Badge color="success">{tx("belgeSablonlari.etkin")}</Badge>
                                  : <Badge color="light" className="text-muted border">{tx("belgeSablonlari.yayindanKalkti")}</Badge>}
                              </td>
                              <td style={{ maxWidth: 260 }}>
                                {adres ? (
                                  <a href={adres} download={t.fileGivenName || true} target="_blank" rel="noreferrer"
                                    className="d-inline-flex align-items-center gap-1 text-truncate" style={{ maxWidth: "100%" }}
                                    title={t.fileGivenName || ""}>
                                    <Download size={13} className="flex-shrink-0" />
                                    <span className="text-truncate">{t.fileGivenName || tx("belgeSablonlari.dosya2")}</span>
                                  </a>
                                ) : (t.fileGivenName || "—")}
                              </td>
                              <td>{t.uploadedBy != null ? `#${t.uploadedBy}` : "—"}</td>
                              <td className="text-nowrap small">{fmtTarih(t.insertDate)}</td>
                              <td className="text-end pe-3 text-nowrap">
                                <Button color="link" size="sm" className="p-1" title={tx("belgeSablonlari.gorunenAdiDegistir")}
                                  onClick={() => islemAc("ad", t)}>
                                  <Pencil size={14} />
                                </Button>
                                {t.isActive ? (
                                  <Button color="link" size="sm" className="p-1 text-danger" title={tx("belgeSablonlari.yayindanKaldir")}
                                    onClick={() => islemAc("kaldir", t)}>
                                    <EyeOff size={14} />
                                  </Button>
                                ) : (
                                  <Button color="link" size="sm" className="p-1" title={tx("belgeSablonlari.yenidenYayinla")}
                                    onClick={() => islemAc("yayinla", t)}>
                                    <RotateCcw size={14} />
                                  </Button>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </React.Fragment>
                    ))}
                  </tbody>
                </Table>
              </div>
            </CardBody>
          </Card>
        )}

        <YukleModal
          acik={yukleAcik}
          kapat={() => setYukleAcik(false)}
          turler={turler}
          fuarlar={fuarlar}
          sinir={sinir}
          sablonlar={sablonlar || []}
          onYayimlandi={(yeni) => {
            setYukleAcik(false);
            setMesaj({ color: "success", text: tx("belgeSablonlari.sablonYayimlandi", { v: yeni?.templateVersion ? ` (v${yeni.templateVersion})` : "" }) });
            setSurum((n) => n + 1);
          }}
        />

        <Modal isOpen={!!islem} toggle={() => !isleniyor && setIslem(null)} centered>
          {islem && (
            <>
              <ModalHeader toggle={() => !isleniyor && setIslem(null)}>
                {islem.tip === "kaldir" ? tx("belgeSablonlari.yayindanKaldir") : islem.tip === "yayinla" ? tx("belgeSablonlari.yenidenYayinla") : tx("belgeSablonlari.gorunenAdiDegistir")}
              </ModalHeader>
              <ModalBody className="small">
                <div className="mb-2">
                  <b>{turBilgisi.get(Number(islem.sablon.fileTypeId))?.fileTypeName || islem.sablon.fileTypeName}</b>
                  {" · "}{SABLON_TURU_ADI[kindKeyToTur(islem.sablon.kindKey)]} · {DIL_ADI[islem.sablon.languageId]}
                  {" · "}{fuarAdi(fuarlar, islem.sablon.fuarId)} · v{islem.sablon.templateVersion}
                </div>
                {islem.tip === "kaldir" && (
                  <>
                    <p className="mb-2">
                      {tx("belgeSablonlari.sablonYayindanKalkarFirmalarBu")}
                    </p>
                    {genelYedek && (
                      <p className="mb-0 text-muted">
                        {tx("belgeSablonlari.buFuardaBundanSonraGenel", { templateVersion: genelYedek.templateVersion })}
                      </p>
                    )}
                  </>
                )}
                {islem.tip === "yayinla" && (
                  yuvadakiEtkin ? (
                    <Alert color="warning" className="py-2 mb-0">
                      {tx("belgeSablonlari.buYuvadakiEtkinSablon")}{" "}<b>v{yuvadakiEtkin.templateVersion}</b> ({yuvadakiEtkin.fileGivenName || tx("belgeSablonlari.adsiz")}{tx("belgeSablonlari.yayindanKalkarFirmalarYenidenV", { templateVersion: islem.sablon.templateVersion })}
                    </Alert>
                  ) : (
                    <p className="mb-0">{tx("belgeSablonlari.buSurumYenidenYayinlanirFirmalar")}</p>
                  )
                )}
                {islem.tip === "ad" && (
                  <FormGroup className="mb-0">
                    <Label for="sablon-ad" className="mb-1">{tx("belgeSablonlari.gorunenAdFirmaBuAdla")}</Label>
                    <Input id="sablon-ad" bsSize="sm" value={yeniAd} maxLength={255} onChange={(e) => setYeniAd(e.target.value)} />
                  </FormGroup>
                )}
                {islemHatasi && <Alert color="danger" className="py-2 mt-3 mb-0">{islemHatasi}</Alert>}
              </ModalBody>
              <ModalFooter>
                <Button color="light" onClick={() => setIslem(null)} disabled={isleniyor}>{tx("belgeSablonlari.vazgec")}</Button>
                <Button
                  color={islem.tip === "kaldir" ? "danger" : "primary"}
                  disabled={isleniyor || (islem.tip === "ad" && (!yeniAd.trim() || yeniAd.trim() === (islem.sablon.fileGivenName || "")))}
                  onClick={islemiYap}
                >
                  {isleniyor && <Spinner size="sm" className="me-1" />}
                  {islem.tip === "kaldir" ? tx("belgeSablonlari.yayindanKaldir") : islem.tip === "yayinla" ? tx("belgeSablonlari.yenidenYayinla") : tx("common:kaydet")}
                </Button>
              </ModalFooter>
            </>
          )}
        </Modal>
      </Container>
    </div>
  );
}
